import { css, cx } from "@emotion/css";
import { borderRadius, borderWidth, space, text, themeTokens } from "@octopusdeploy/design-system-tokens";
import React, { forwardRef, useCallback, useEffect, useState } from "react";
interface ListboxOption {
    label: string;
    value: string;
}
interface ListboxProps {
    id: string;
    value: string | undefined;
    options: ListboxOption[];
    accessibleName: string;
    onOptionSelected: (option: ListboxOption) => void;
    getOptionId: (index: number) => string;
    onOptionActive?: (index: number) => void;
    onTabPressed?: (event: React.KeyboardEvent) => void;
}
export const Listbox = forwardRef<HTMLUListElement | null, ListboxProps>(({ id, value, options, accessibleName, onOptionSelected, getOptionId, onOptionActive, onTabPressed }, ref) => {
    const [activeIndex, setActiveIndex] = useState(Math.max(options.findIndex((option) => option.value === value), 0));
    useEffect(() => {
        onOptionActive?.(activeIndex);
    }, [activeIndex, onOptionActive]);
    const handleKeyDown = (event: React.KeyboardEvent) => {
        const { key } = event;
        if (key === "ArrowUp" || key === "ArrowDown") {
            event.preventDefault();
            setActiveIndex((i) => {
                const direction = key === "ArrowUp" ? -1 : 1;
                return (i + direction + options.length) % options.length;
            });
        }
        else if (key === "Home" || key === "End") {
            event.preventDefault();
            setActiveIndex(key === "Home" ? 0 : options.length - 1);
        }
        else if (key === "Enter" || key === " ") {
            event.preventDefault();
            if (activeIndex >= 0 && activeIndex < options.length) {
                onOptionSelected(options[activeIndex]);
            }
        }
        else if (key === "Tab") {
            onTabPressed?.(event);
        }
    };
    const onSelectedOptionRef = useCallback((element: HTMLElement | null) => {
        element?.focus();
    }, []);
    return (<ul ref={ref} id={id} className={optionsContainerStyles} role="listbox" aria-label={accessibleName} onKeyDown={handleKeyDown}>
            {options.map((option, index) => (<li ref={activeIndex === index ? onSelectedOptionRef : undefined} key={option.value} id={getOptionId(index)} role="option" tabIndex={activeIndex === index ? 0 : -1} aria-selected={value === option.value} className={cx(optionStyles, {
                [selectedOptionStyles]: value === option.value,
                [focusedOptionStyles]: activeIndex === index,
            })} onClick={() => onOptionSelected(option)} onMouseEnter={() => setActiveIndex(index)}>
                    {option.label}
                </li>))}
        </ul>);
});
Listbox.displayName = "Listbox";
const optionsContainerStyles = css({
    display: "flex",
    flexDirection: "column",
    gap: space[4],
    borderRadius: borderRadius.small,
    border: `1px solid ${themeTokens.color.border.primary}`,
    backgroundColor: themeTokens.color.background.primary.default,
    boxShadow: themeTokens.shadow.medium,
    maxHeight: "300px",
    overflowY: "auto",
    padding: space[6],
    width: "100%",
    boxSizing: "border-box",
    margin: 0,
});
const optionStyles = css({
    flexShrink: 0,
    padding: `${space[8]}`,
    cursor: "pointer",
    borderRadius: borderRadius.small,
    font: text.body.regular.medium,
    color: themeTokens.color.text.primary,
    // delay the border-left color to avoid a flash as the popover closes
    transition: "background-color 0.15s ease, border-left-color 0.15s ease-out 0.15s",
    borderLeft: `${borderWidth[4]} solid transparent`,
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    overflow: "hidden",
    ":focus-visible": {
        outline: "none",
    },
    "&:active": {
        backgroundColor: themeTokens.color.menuList.background.active,
    },
});
const selectedOptionStyles = css({
    backgroundColor: themeTokens.color.menuList.background.hover,
    borderLeftColor: themeTokens.color.border.selected,
});
const focusedOptionStyles = css({
    backgroundColor: themeTokens.color.menuList.background.hover,
});
